import { stores } from '@kurtosys/app-start';
import { components } from '@kurtosys/ksys-app-components';
import { IManifest } from '@kurtosys/types/appsManager/IManifest.js';
import { action, computed, makeObservable, observable } from 'mobx';

import { IComponentStyles } from '../../../models/app/IComponentStyles.js';
import { IConfiguration } from '../../../models/app/IConfiguration.js';
import { TStoreContext } from '../../../models/app/TStoreContext.js';
import { AppStore } from '../../App/stores/AppStore.js';
import { IAllButtonProps } from '../models/IAllButtonProps.js';
import { IHeaderOptionsProps } from '../models/IHeaderOptionsProps.js';
import { IColumnSelectorConfiguration, ISearchInput } from '../models/index.js';
import { ISelectableColumnsConfig } from '../models/ISelectableColumnsConfig.js';
import { ISelectableColumnsProps } from '../models/ISelectableColumnsProps.js';

export type IDropdownItem = components.base.Dropdown.models.IDropdownItem;

export class ColumnSelectorStore extends stores.base.StoreBase<IConfiguration, IComponentStyles> {
	static componentKey = 'columnSelector' as const;

	@observable columnsMap: Map<string, ISelectableColumnsProps> | null = null;

	constructor(storeContext: TStoreContext, manifest: IManifest) {
		super(storeContext, manifest);
		makeObservable(this);
	}

	@computed
	get appStore() {
		return this.storeContext.get<AppStore>('appStore');
	}

	@computed
	get storageKey(): string {
		const ksysAppTemplateId = this.appStore.appParamsHelper?.rawAppParams?.ksysAppTemplateId || 'ksys-app-table';
		const configurationKey = this.appStore.appParamsHelper?.rawAppParams?.configurationKey || 'default';
		return `${ksysAppTemplateId}-${configurationKey}` || 'selectedColumns';
	}

	@computed
	get localStorageValues(): string[] | undefined {
		return JSON.parse(localStorage.getItem(this.storageKey));
	}

	setLocalStorageValues(localStorageValues: string[]): void {
		localStorage.setItem(this.storageKey, JSON.stringify(localStorageValues));
	}

	@action
	async initialize(): Promise<void> {
		const selectableColumnsConfig = this.configuration?.dropdownOptions?.selectableColumns;
		const localStorageValues: string[] = [];
		// TODO: build up selectable columns from table store columns. do we want to do this?
		if (selectableColumnsConfig && selectableColumnsConfig.length > 0) {
			this.columnsMap = new Map<string, ISelectableColumnsProps>();
			// Create a map of the selectable columns from the configuration
			selectableColumnsConfig.map((item: ISelectableColumnsConfig) => {
				const key = this.getQueryValue(item.key);
				const label = this.getQueryValue(item.label);
				const column: ISelectableColumnsProps = {
					value: key,
					label,
					selected: false,
				};

				// If there are values in localStorage, we use those values, otherwise we use the default values from the configuration
				if (this.localStorageValues?.length > 0) {
					column.selected = this.localStorageValues?.indexOf(key) >= 0;
				} else {
					column.selected = item.selected ?? true;
				}

				if (column.selected) {
					localStorageValues.push(key);
				}
				this.columnsMap.set(column.value, column);
			});

			this.setLocalStorageValues(localStorageValues);
		}
	}

	/**
	 * Handles the selection of a column
	 * @param items - The selected items
	 * @memberof ColumnSelectorStore
	 * @returns {void}
	 */
	@action
	onItemSelect = (items: IDropdownItem | IDropdownItem[] | undefined = []) => {
		if (!Array.isArray(items)) {
			items = [items];
		}
		// reset all selected items since we have a new list of selected items
		this.clearAll();

		const localStorageValues: string[] = [];

		// set selected items
		for (const item of items) {
			const column = this.columnsMap.get(item.value);
			if (column) {
				column.selected = true;
				localStorageValues.push(column.value);
			}
		}
		this.setLocalStorageValues(localStorageValues);
	};

	/**
	 * Clears all columns
	 * @memberof ColumnSelectorStore
	 * @returns {void}
	 */
	@action
	clearAll = (): void => {
		this.columnsMap.forEach((value, key) => {
			value.selected = false;
		});
		this.setLocalStorageValues([]);
	};

	/**
	 * Selects all columns
	 * @memberof ColumnSelectorStore
	 * @returns {void}
	 */
	@action
	selectAll = (): void => {
		const localStorageValues: string[] = [];
		this.columnsMap.forEach((value, key) => {
			value.selected = true;
			localStorageValues.push(key);
		});
		this.setLocalStorageValues(localStorageValues);
	};

	@computed
	get configuration(): IColumnSelectorConfiguration | undefined {
		const appStore = this.storeContext.get<AppStore>('appStore');
		if (this.storeContext && appStore) {
			return appStore.getComponentConfiguration(ColumnSelectorStore.componentKey);
		}
	}

	/**
	 * Returns the columns needed for the Dropdown component
	 * @returns {IDropdownItem[]}
	 * @memberof ColumnSelectorStore
	 * @readonly
	 */
	@computed
	get columns(): IDropdownItem[] {
		// de-reference the items so that the dropdown doesn't cause mobx issues
		return Array.from(this.columnsMap?.values() || []).map((item) => {
			return {
				...item,
				highlight: true,
			};
		});
	}

	/**
	 * Returns the selected columns needed for the Dropdown component
	 * @returns {IDropdownItem[]}
	 * @memberof ColumnSelectorStore
	 * @readonly
	 */
	@computed
	get selectedColumns(): IDropdownItem[] {
		return this.columns.filter((item) => item.selected);
	}

	@computed
	get showColumnSelector(): boolean {
		return (
			this.configuration?.enabled !== false && this.configuration?.dropdownOptions?.selectableColumns?.length > 0
		);
	}

	/**
	 * Returns the selected placeholder text, set to null if not defined to disable the selected placeholder
	 * @returns {string}
	 * @memberof ColumnSelectorStore
	 * @readonly
	 * @default null
	 * @title Selected Placeholder
	 * @description The selected placeholder text which will show when items are selected in the dropdown
	 */
	@computed
	get selectedPlaceholder(): string | null {
		return this.getQueryValue(this.configuration?.headerOptions?.selectedPlaceholder) || null;
	}

	/**
	 * Returns the placeholder text, set to null if not defined to disable the placeholder
	 * @returns {string}
	 * @memberof ColumnSelectorStore
	 * @readonly
	 * @default null
	 * @title Placeholder
	 * @description The placeholder text which will show when no items are selected in the dropdown
	 */
	@computed
	get placeholder(): string | null {
		return this.getQueryValue(this.configuration?.headerOptions?.placeholder) || null;
	}

	@computed
	get listHeading(): string {
		const listHeading = this.configuration?.dropdownOptions?.listHeading;
		if (listHeading) {
			return this.getQueryValue(listHeading);
		}
		return '';
	}

	@computed
	get selectAllButton(): IAllButtonProps | undefined {
		const selectAllButton = this.configuration?.dropdownOptions?.inputs?.selectAllButton;
		if (selectAllButton) {
			const { enable, text } = selectAllButton;
			const selectAllButtonProps = {
				enable,
				text: this.getQueryValue(text),
				onClick: this.selectAll,
			};
			return selectAllButtonProps;
		}
		return;
	}

	@computed
	get clearAllButton(): IAllButtonProps | undefined {
		const clearAllButton = this.configuration?.dropdownOptions?.inputs?.clearAllButton;
		if (clearAllButton) {
			const { enable, text } = clearAllButton;
			const clearAllButtonProps = {
				enable,
				text: this.getQueryValue(text),
				onClick: this.clearAll,
			};
			return clearAllButtonProps;
		}
		return;
	}

	@computed
	get headerOptions(): IHeaderOptionsProps | undefined {
		const headerOptions = this.configuration?.headerOptions;
		if (headerOptions) {
			const {
				labelOptions: { hideLabel = false },
				iconOptions: { enable, src, asset },
			} = headerOptions;

			const headerOptionsProps: IHeaderOptionsProps = {
				labelOptions: {
					label:
						this.getQueryValue(this.configuration?.headerOptions?.labelOptions?.label) || 'Select Columns',
					hideLabel,
				},
				iconOptions: {
					enable,
					asset: null,
					src: null,
				},
			};

			if (asset) {
				headerOptionsProps.iconOptions.asset = asset;
			}

			if (src) {
				headerOptionsProps.iconOptions.src = this.getQueryValue(src);
			}

			return headerOptionsProps;
		}
		return;
	}

	@computed
	get searchInput(): ISearchInput | undefined {
		const searchInput = this.configuration?.dropdownOptions?.inputs?.search;
		if (searchInput) {
			return searchInput;
		}
		return;
	}
}
